import { Injector } from '@angular/core';
import { IFile } from 'src/app/interfaces/io/file.interface';
import { IFileDriver } from 'src/app/services/io/drivers/file-driver.interface';
import { AngularFireStorage } from '@angular/fire/storage';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FileModel } from 'src/app/models/io/file-model';
import { IconsService } from 'src/app/services/ux/icons.service';

export class FirebaseStorageDriver implements IFileDriver {
  unit: string;
  name: string;

  private authSv: AuthService;
  private storage: AngularFireStorage;
  private iconSv: IconsService;
  private root = "";
  private structure: any;

  constructor() {
  }

  async mount(unit: string, settingsUrl: string, injector: Injector) {
    this.authSv = this.authSv || injector.get(AuthService);
    this.storage = this.storage || injector.get(AngularFireStorage);
    this.iconSv = this.iconSv || injector.get(IconsService);

    const response = await fetch(settingsUrl);
    const settings = await response.json();

    this.root = settings.root;
    this.structure = settings.structure;

    if (this.root.includes("$userId")) {
      if (!this.authSv.isLogged) {
        throw new Error("You must be logged to mount this unit.");
      }

      this.root = this.root.split("$userId").join(this.authSv.user.id);
    }
  }

  isReadOnly() {
    return false;
  }

  getDriveType(): string {
    return "Firebase Storage";
  }

  async exists(path: string): Promise<boolean> {
    const ref = this.getRef(path);

    return new Promise((resolve) => {
      ref.getDownloadURL().subscribe(() => {
        resolve(true);
      }, _ => {
        resolve(false);
      });
    });
  }

  async rmdir(path: string): Promise<void> {
    const ref = this.getRef(path);
    await ref.delete().toPromise();
  }

  async unlink(path: string): Promise<void> {
    const ref = this.getRef(path);
    await ref.delete().toPromise();
  }

  private pathToFirebasePath(path: string) {
    let adaptedPath: string;

    if (path.indexOf(":/") !== -1) {
      adaptedPath = path.substr(path.indexOf(":/") + 2);
    }
    else if (path.indexOf(":") !== -1) {
      adaptedPath = path.substr(path.indexOf(":") + 1);
    }
    else {
      adaptedPath = path;
    }

    return adaptedPath;
  }

  async list(path: string): Promise<IFile[]> {
    const adaptedPath = this.pathToFirebasePath(path);

    if (adaptedPath === "") {
      return Object.keys(this.structure).map(k => {
        const file: IFile = new FileModel({
          icon: "folder",
          path: path + "/" + k,
          size: 0,
          security: this.structure[k].security,
          properties: {
            isDirectory: true
          }
        });

        return file;
      });
    }

    // console.log("List", path, this.root + "/" + adaptedPath);

    const ref = this.storage.ref(this.root + "/" + adaptedPath);
    const all = await ref.listAll().toPromise();
    const files: IFile[] = [];

    for (const folderRef of all.prefixes) {
      const file = new FileModel();
      file.path = folderRef.name;
      file.security = {
        authentication: true,
        readOnly: false
      };

      file.icon = "folder";
      file.size = 1;
      file.properties = {
        isDirectory: true
      };

      files.push(file);
    }

    for (const itemRef of all.items) {
      const file = new FileModel();
      file.path = this.root + "/" + itemRef.fullPath;
      file.security = {
        authentication: true,
        readOnly: false
      };

      let meta = await itemRef.getMetadata();
      if (!meta) {
        meta = {};
      }

      /** @todo Assign icons shouldn't be responsability of the driver */
      file.icon = this.iconSv.getIconByFormat(file.path);
      file.size = meta.size || 1;
      file.properties = {
        isDirectory: false
      };

      files.push(file);
    }

    return files;
  }

  async readTextFile(path: string): Promise<string> {
    const ref = this.getRef(path);
    const url = await ref.getDownloadURL().toPromise();
    const content = await this.downloadTextFile(url);

    return content;
  }

  async writeTextFile(path: string, content: string): Promise<void> {
    const ref = this.getRef(path);
    await ref.putString(content, "raw", { contentType: "text/plain" });
  }

  private getRef(path: string) {
    const adaptedPath = this.pathToFirebasePath(path);
    const ref = this.storage.ref(this.root + "/" + adaptedPath);
    return ref;
  }

  private async downloadTextFile(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'text';
      xhr.onload = () => resolve(xhr.response);
      xhr.onerror = err => reject(err);
      xhr.open('GET', url);
      xhr.send();
    });
  }
}
